import { type AxiosRequestConfig } from "axios";

import { loqateAPI } from "@/api-v3/mutator/axios-instance-v3";
import { ENV } from "@/features/config";

interface LoqateValidationItem {
  IsValid: string;
  ResponseCode: string;
}

interface LoqateValidationResponse {
  Items: LoqateValidationItem[];
}

enum ValidationStatus {
  VALID = "VALID",
  NOT_VALID = "NOT_VALID",
  IN_PROGRESS = "IN_PROGRESS",
}

const emailValidationService = (email: string): AxiosRequestConfig => {
  return {
    url: "/EmailValidation/Interactive/Validate/v2.00/json3.ws",
    method: "GET",
    params: {
      Key: ENV.LOQATE.KEY,
      Email: email,
    },
  };
};

const phoneValidationService = (
  phone: string,
  countryCode: string
): AxiosRequestConfig => {
  return {
    url: "/PhoneNumberValidation/Interactive/Validate/v2.2/json3.ws",
    method: "GET",
    params: {
      Key: ENV.LOQATE.KEY,
      Phone: phone,
      Country: countryCode,
    },
  };
};

// Caches for validated emails and phones
const emailValidationCache: Record<string, LoqateValidationResponse> = {};
const phoneValidationCache: Record<string, LoqateValidationResponse> = {};

const validateEmail = async (email: string) => {
  if (emailValidationCache[email]) {
    const responseData = emailValidationCache[email];
    return parseEmailResponse(responseData);
  }

  try {
    const response = await loqateAPI(emailValidationService(email));
    emailValidationCache[email] = response.data;
    return parseEmailResponse(response.data as LoqateValidationResponse);
  } catch {
    throw new Error("Error during email validation");
  }
};

const validatePhone = async (phone: string, countryCode: string) => {
  if (phoneValidationCache[phone]) {
    const responseData = phoneValidationCache[phone];
    return parsePhoneResponse(responseData);
  }

  try {
    const response = await loqateAPI(
      phoneValidationService(phone, countryCode)
    );
    phoneValidationCache[phone] = response.data;
    return parsePhoneResponse(response.data as LoqateValidationResponse);
  } catch {
    throw new Error("Error during phone validation");
  }
};

const parsePhoneResponse = (responseData: LoqateValidationResponse) => {
  const items = responseData.Items;
  if (items.length === 0) {
    return ValidationStatus.NOT_VALID;
  } else if (items[0].IsValid === "No") {
    return ValidationStatus.NOT_VALID;
  } else {
    return ValidationStatus.VALID;
  }
};

const parseEmailResponse = (responseData: LoqateValidationResponse) => {
  const items = responseData.Items;
  if (items.length === 0) {
    return ValidationStatus.NOT_VALID;
  } else if (items[0].ResponseCode === "Invalid") {
    return ValidationStatus.NOT_VALID;
  } else {
    return ValidationStatus.VALID;
  }
};

export const asyncValidators = {
  email: async (value: string) => {
    // If Loqate is disabled or unconfigured, skip validation
    if (!ENV.LOQATE.ENABLED || !ENV.LOQATE.KEY) {
      return null;
    }

    try {
      const result = await validateEmail(value);
      return result === ValidationStatus.VALID ? null : "Invalid email";
    } catch {
      return "Email validation failed";
    }
  },
  phone: async (value: string) => {
    // If Loqate is disabled or unconfigured, skip validation
    if (!ENV.LOQATE.ENABLED || !ENV.LOQATE.KEY) {
      return null;
    }

    try {
      const result = await validatePhone(value, "USA");
      return result === ValidationStatus.VALID ? null : "Invalid phone number";
    } catch {
      return "Phone validation failed";
    }
  },
};
